import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { ExternalLink, FileDown, Loader2, Presentation, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/AppShell";
import { FeatureGate } from "@/components/UpgradeGate";
import { DeckCharts } from "@/components/reports/DeckCharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import {
  DECK_INSTRUCTIONS_MAX,
  DECK_AUDIENCES,
  DECK_KINDS,
  DECK_KIND_LABEL,
  defaultDeckParams,
  type DeckAmount,
  type DeckBrief,
  type DeckExport,
  type DeckImagery,
  type DeckKind,
  type DeckParams,
} from "@/lib/gamma";
import { uploadDeckImages } from "@/lib/gamma-images";
import { PALETTE_HEX, PALETTE_LABEL } from "@/lib/visuals/palette-hex";
import type { PaletteName } from "@/lib/visuals/types";
import {
  buildDeckBrief,
  deleteDeck,
  getDeckAllowance,
  listDecks,
  pollDeckGeneration,
} from "@/lib/gamma.functions";
import { planErrorMessage } from "@/lib/plan-errors";

export const Route = createFileRoute("/_authenticated/reports/")({
  validateSearch: (search: Record<string, unknown>): { case?: string; visual?: string } => ({
    ...(typeof search["case"] === "string" ? { case: search["case"] } : {}),
    ...(typeof search["visual"] === "string" ? { visual: search["visual"] } : {}),
  }),

  head: () => ({
    meta: [
      { title: "Slide reports — MyPriorities" },
      {
        name: "description",
        content:
          "Turn your team task list, delivery timeline, saved visuals or performance data into a Gamma slide deck in a couple of clicks.",
      },
      { property: "og:title", content: "Slide reports — MyPriorities" },
      {
        property: "og:description",
        content: "Generate presentation-ready decks straight from your live hub data.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ReportsRoute,
});

function ReportsRoute() {
  return (
    <FeatureGate
      requires="pro"
      title="Slide reports"
      subtitle="Pick a report, review the outline we build from your data, then let Gamma design the deck."
    >
      <Reports />
    </FeatureGate>
  );
}

const AMOUNTS: { value: DeckAmount; label: string }[] = [
  { value: "brief", label: "Brief" },
  { value: "medium", label: "Balanced" },
  { value: "detailed", label: "Detailed" },
];

const IMAGERY: { value: DeckImagery; label: string }[] = [
  { value: "noImages", label: "No stock imagery" },
  { value: "themeAccent", label: "Theme accents" },
  { value: "webFreeToUse", label: "Free-to-use photos" },
  { value: "aiGenerated", label: "AI illustrations" },
];

const EXPORTS: { value: DeckExport; label: string }[] = [
  { value: "none", label: "Gamma link only" },
  { value: "pptx", label: "Also PowerPoint" },
  { value: "pdf", label: "Also PDF" },
];

function Reports() {
  const queryClient = useQueryClient();
  const { case: caseFromUrl, visual: visualFromUrl } = Route.useSearch();
  const [params, setParams] = useState<DeckParams>(() => {
    if (caseFromUrl) return { ...defaultDeckParams("business-case"), caseId: caseFromUrl };
    // Arriving from a Sales page's "Create slides" action: preselect that visual.
    if (visualFromUrl) return { ...defaultDeckParams("visuals"), visualIds: [visualFromUrl] };
    return defaultDeckParams();
  });
  const [brief, setBrief] = useState<DeckBrief | null>(null);
  const [briefText, setBriefText] = useState("");
  const [title, setTitle] = useState("");
  const nodes = useRef(new Map<string, HTMLDivElement>());

  const set = <K extends keyof DeckParams>(key: K, value: DeckParams[K]) =>
    setParams((prev) => ({ ...prev, [key]: value }));

  const allowance = useQuery({ queryKey: ["deck-allowance"], queryFn: () => getDeckAllowance() });

  const decks = useQuery({
    queryKey: ["decks"],
    queryFn: () => listDecks(),
    refetchInterval: (query) =>
      (query.state.data ?? []).some((deck) => deck.status === "pending") ? 6000 : false,
  });

  const visuals = useQuery({
    queryKey: ["visuals", "picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("visuals")
        .select("id, title, kind, updated_at")
        .order("updated_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
    enabled: params.kind === "visuals",
  });

  const cases = useQuery({
    queryKey: ["business_cases", "picker"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("business_cases")
        .select("id, title, updated_at")
        .order("updated_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data;
    },
    enabled: params.kind === "business-case",
  });

  const build = useMutation({
    mutationFn: () => buildDeckBrief({ data: { params } }),
    onSuccess: (result) => {
      setBrief(result);
      setBriefText(result.brief);
      setTitle(result.title);
    },
    onError: (error: Error) => toast.error(planErrorMessage(error, "Couldn't build that outline.")),
  });

  const generate = useMutation({
    mutationFn: async () => {
      const specs = params.includeCharts ? (brief?.images ?? []) : [];
      const chartNodes = specs
        .map((spec) => ({ key: spec.key, node: nodes.current.get(spec.key) }))
        .filter((entry): entry is { key: string; node: HTMLDivElement } => Boolean(entry.node));
      const imageUrls = await uploadDeckImages(chartNodes);
      const { startDeckGeneration } = await import("@/lib/gamma.functions");
      return startDeckGeneration({ data: { params, title, brief: briefText, imageUrls } });
    },
    onSuccess: async ({ id }) => {
      toast.success("Gamma is building your deck — this usually takes under a minute.");
      await queryClient.invalidateQueries({ queryKey: ["decks"] });
      await queryClient.invalidateQueries({ queryKey: ["deck-allowance"] });
      void pollDeckGeneration({ data: { id } }).then(() =>
        queryClient.invalidateQueries({ queryKey: ["decks"] }),
      );
    },
    onError: (error: Error) => toast.error(planErrorMessage(error, "Couldn't start that deck.")),
  });

  const refresh = useMutation({
    mutationFn: (id: string) => pollDeckGeneration({ data: { id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["decks"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteDeck({ data: { id } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["decks"] }),
  });

  const left = allowance.data ? allowance.data.remaining : null;

  return (
    <AppShell
      title="Slide reports"
      subtitle="Built with Gamma from your live hub data — no invented numbers."
      action={
        <Button variant="outline" asChild>
          <Link to="/visuals">Visuals hub</Link>
        </Button>
      }
    >
      <div className="space-y-6">
        <section className="rounded-xl border border-border bg-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg font-semibold">1. Choose a report</h2>
            {left !== null ? (
              <p className="text-xs text-muted-foreground">
                {left} of {allowance.data?.limit} decks left this month
              </p>
            ) : null}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {DECK_KINDS.map((option) => (
              <button
                key={option.kind}
                type="button"
                onClick={() => {
                  set("kind", option.kind as DeckKind);
                  setBrief(null);
                }}
                className={`rounded-lg border p-3 text-left transition ${
                  params.kind === option.kind
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <p className="text-sm font-medium">{option.label}</p>
                <p className="mt-1 text-xs text-muted-foreground">{option.hint}</p>
              </button>
            ))}
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="deck-days">
                {params.kind === "performance" ? "Look back (days)" : "Horizon (days)"}
              </Label>
              <Input
                id="deck-days"
                type="number"
                min={7}
                max={365}
                value={params.days}
                onChange={(e) => set("days", Number(e.target.value) || 30)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="deck-cards">Slides</Label>
              <Input
                id="deck-cards"
                type="number"
                min={3}
                max={40}
                value={params.numCards}
                onChange={(e) => set("numCards", Number(e.target.value) || 10)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Detail</Label>
              <Select value={params.amount} onValueChange={(v) => set("amount", v as DeckAmount)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AMOUNTS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="deck-audience">Audience</Label>
              <Input
                id="deck-audience"
                value={params.audience}
                onChange={(e) => set("audience", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="deck-theme">Gamma theme (optional)</Label>
              <Input
                id="deck-theme"
                placeholder="e.g. Oasis"
                value={params.themeName}
                onChange={(e) => set("themeName", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Colour palette</Label>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => set("palette", "")}
                  className={
                    "rounded-md border px-2 py-1.5 text-xs " +
                    (params.palette === "" ? "border-primary" : "border-border hover:bg-accent")
                  }
                >
                  Gamma theme default
                </button>
                {(Object.keys(PALETTE_HEX) as PaletteName[]).map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => set("palette", name)}
                    title={PALETTE_LABEL[name]}
                    aria-label={`${PALETTE_LABEL[name]} palette`}
                    className={
                      "flex items-center gap-1 rounded-md border p-1.5 " +
                      (params.palette === name ? "border-primary" : "border-border hover:bg-accent")
                    }
                  >
                    {PALETTE_HEX[name].slice(0, 5).map((c) => (
                      <span key={c} className="size-3 rounded-full" style={{ background: c }} />
                    ))}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Imagery</Label>
              <Select
                value={params.imagery}
                onValueChange={(v) => set("imagery", v as DeckImagery)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMAGERY.map((i) => (
                    <SelectItem key={i.value} value={i.value}>
                      {i.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Export</Label>
              <Select
                value={params.exportAs}
                onValueChange={(v) => set("exportAs", v as DeckExport)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPORTS.map((e) => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
              <Label htmlFor="deck-charts" className="text-sm font-normal">
                Include chart images
              </Label>
              <Switch
                id="deck-charts"
                checked={params.includeCharts}
                onCheckedChange={(v) => set("includeCharts", v)}
              />
            </div>
            {params.kind === "team-tasks" ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
                <Label htmlFor="deck-team" className="text-sm font-normal">
                  Team tasks only
                </Label>
                <Switch
                  id="deck-team"
                  checked={params.teamOnly}
                  onCheckedChange={(v) => set("teamOnly", v)}
                />
              </div>
            ) : null}
          </div>

          {params.kind === "business-case" ? (
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Saved business case</Label>
                <Select
                  {...(params.caseId ? { value: params.caseId } : {})}
                  onValueChange={(v) => {
                    set("caseId", v);
                    setBrief(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Pick a case" />
                  </SelectTrigger>
                  <SelectContent>
                    {(cases.data ?? []).map((row) => (
                      <SelectItem key={row.id} value={row.id}>
                        {row.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!cases.isLoading && !(cases.data ?? []).length ? (
                  <p className="text-xs text-muted-foreground">
                    No saved cases yet — build one in the business case tool first.
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label>Deck audience</Label>
                <div className="grid gap-2">
                  {DECK_AUDIENCES.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        set("deckAudience", option.value);
                        setBrief(null);
                      }}
                      className={`rounded-lg border p-2 text-left transition ${
                        params.deckAudience === option.value
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <p className="text-sm font-medium">{option.label}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{option.hint}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {params.kind === "visuals" ? (
            <div className="mt-5">
              <Label>Visuals to include</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {(visuals.data ?? []).map((visual) => {
                  const checked = params.visualIds.includes(visual.id);
                  return (
                    <button
                      key={visual.id}
                      type="button"
                      onClick={() =>
                        set(
                          "visualIds",
                          checked
                            ? params.visualIds.filter((id) => id !== visual.id)
                            : [...params.visualIds, visual.id],
                        )
                      }
                      className={`rounded-lg border p-2 text-left text-sm transition ${
                        checked
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      {visual.title}
                    </button>
                  );
                })}
                {!visuals.isLoading && !(visuals.data ?? []).length ? (
                  <p className="text-sm text-muted-foreground">
                    No saved visuals yet — build one in the Visuals hub first.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="mt-5 space-y-1.5">
            <Label htmlFor="deck-instructions">Your brief (optional)</Label>
            <Textarea
              id="deck-instructions"
              className="min-h-[100px]"
              maxLength={DECK_INSTRUCTIONS_MAX}
              placeholder="Tell Gamma what this deck is for — e.g. “Monthly client review for Acme: lead on delivery risk and SLA breaches, keep it to 8 slides, no fluff.”"
              value={params.instructions}
              onChange={(e) => set("instructions", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Shapes the outline we build and is passed to Gamma as extra design instructions.
            </p>
          </div>

          <Button className="mt-5" onClick={() => build.mutate()} disabled={build.isPending}>
            {build.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
            Build the outline
          </Button>
        </section>

        {brief ? (
          <section className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg font-semibold">2. Review and generate</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Edit anything below — Gamma designs from exactly this content.
              {brief.images.length && params.includeCharts
                ? ` ${brief.images.length} chart image${brief.images.length === 1 ? "" : "s"} will be attached.`
                : ""}
            </p>
            <div className="mt-4 space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="deck-title">Deck title</Label>
                <Input id="deck-title" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="deck-brief">Outline</Label>
                <Textarea
                  id="deck-brief"
                  className="min-h-[280px] font-mono text-xs"
                  value={briefText}
                  onChange={(e) => setBriefText(e.target.value)}
                />
              </div>
            </div>
            <Button
              className="mt-4"
              onClick={() => generate.mutate()}
              disabled={generate.isPending || left === 0}
            >
              {generate.isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <Presentation className="mr-2 size-4" />
              )}
              Generate the deck
            </Button>
            <DeckCharts
              specs={params.includeCharts ? brief.images : []}
              register={(key, node) => {
                if (node) nodes.current.set(key, node);
                else nodes.current.delete(key);
              }}
            />
          </section>
        ) : null}

        <section className="rounded-xl border border-border bg-card p-5">
          <h2 className="font-display text-lg font-semibold">Your decks</h2>
          {decks.isLoading ? (
            <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
          ) : !(decks.data ?? []).length ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Nothing generated yet. Build an outline above to make your first deck.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-border">
              {(decks.data ?? []).map((deck) => (
                <li key={deck.id} className="flex flex-wrap items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{deck.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {DECK_KIND_LABEL[deck.kind as DeckKind] ?? deck.kind} ·{" "}
                      {new Date(deck.created_at).toLocaleString("en-GB")} ·{" "}
                      {deck.status === "pending"
                        ? "Building…"
                        : deck.status === "failed"
                          ? (deck.error ?? "Failed")
                          : "Ready"}
                    </p>
                  </div>
                  {deck.deck_url ? (
                    <Button size="sm" asChild>
                      <a href={deck.deck_url} target="_blank" rel="noreferrer">
                        <ExternalLink className="mr-1.5 size-4" /> Open
                      </a>
                    </Button>
                  ) : null}
                  {deck.export_url ? (
                    <Button size="sm" variant="outline" asChild>
                      <a href={deck.export_url} target="_blank" rel="noreferrer">
                        <FileDown className="mr-1.5 size-4" /> File
                      </a>
                    </Button>
                  ) : null}
                  {deck.status === "pending" ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => refresh.mutate(deck.id)}
                      disabled={refresh.isPending}
                    >
                      <RefreshCw className="mr-1.5 size-4" /> Check
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" asChild>
                    <Link to="/reports/$id" params={{ id: deck.id }}>
                      Details
                    </Link>
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Delete deck"
                    onClick={() => remove.mutate(deck.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
}
